import { Component, EventEmitter, Input, OnChanges, Output, QueryList, ViewChildren } from '@angular/core';
import { Population, Block, Condition, ConditionMap } from '../../_models/population';
import { QuestionsService } from '_services/questions.service';
import { SelectorsService } from '_services/selectors.service';
import { CalcConfigService } from '_services/calc-config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCard, MatCardContent } from '@angular/material/card';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatInput, MatHint, MatError } from '@angular/material/input';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { QuillEditorComponent } from 'ngx-quill';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { InputSelectorComponent } from '../input-selector/input-selector.component';
import { MatSelect, MatOption } from '@angular/material/select';
import { QuillA11yDirective } from '../../_shared/directives/quill-a11y.directive';
import { KeyboardScrollableDirective } from '../../_shared/directives/keyboard-scrollable.directive';
import { take } from 'rxjs/operators';

@Component({
    selector: 'app-population',
    templateUrl: './population.component.html',
    styleUrls: ['./population.component.css'],
    imports: [MatCard, MatButton, MatFormField, MatLabel, MatInput, ReactiveFormsModule, FormsModule, QuillEditorComponent, QuillA11yDirective, MatCardContent, MatRadioGroup, MatRadioButton, NgClass, InputSelectorComponent, MatSelect, MatOption, MatHint, MatError, KeyboardScrollableDirective]
})
export class PopulationComponent implements OnChanges {

  @ViewChildren('inputSelector') inputSelectors = new QueryList<InputSelectorComponent>();

  private readonly conditionHeaderPrefix = 'population-condition-';
  private readonly conditionRowPrefix = 'population-condition-row-';
  private readonly removeConditionLabelPrefix = 'population-remove-condition-';
  private readonly columnHeaderMap = {
    question: 'population-question-header',
    operator: 'population-operator-header',
    assertion: 'population-assertion-header'
  } as const;

  bindings = {
    // This will overwrite the default binding also named 'tab'
    tab: {
      key: 9,
      handler: function () {
        return true;
      }
    }
  };

  modules = {
    clipboard: {
      matchVisual: false
    },
    keyboard: {
      bindings: this.bindings
    },
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons

      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'indent': '-1' }, { 'indent': '+1' }],          // outdent/indent

      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],

      [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
      [{ 'align': [] }],

      ['clean'],                                         // remove formatting button

      ['link']                         // link and image, video
    ]
  };

  constructor(
    public qs: QuestionsService,
    public cc: CalcConfigService,
    private ss: SelectorsService,
    private snackBar: MatSnackBar,
  ) { }
  conditionMap = ConditionMap;

  @Input({ required: true }) pop!: Population;
  @Input() editMode = false;
  @Input() isNew = false;
  @Output() edit = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() savePopulation = new EventEmitter<void>();
  @Output() cancelEdit = new EventEmitter<void>();

  ngOnChanges() {
    // Population Questions
    for (const block of this.pop.blocks) {
      for (const cond of block.conditions) {
        if (cond.type === 'Question') {
          cond.question = this.qs.getQuestion(cond.keyId);
        }
      }
    }
  }

  getSelectorName(id: string): string {
    const selector = this.ss.getSelector(id);
    return selector ? selector.label : '';
  }

  addCondition(block: Block) {
    const ruleOrder = block.conditions.length > 0 ? block.conditions[block.conditions.length - 1].order + 1 : 1;
    const aCond: Condition = {
      type: 'Question',
      // keyId?: string; // e.x. maps to questionId if NPCInput
      // formulaId?: string;
      // formula?: Formula;
      // question?: Question;
      format: 'Number',
      operator: 0,
      value: '',
      // value2?: string;     // used for "between" comparisons as upper bound
      // values?: string[];  // If value has comma separated values than split them into this array
      order: ruleOrder
    };
    block.conditions.push(aCond);

    this.inputSelectors.changes.pipe(take(1)).subscribe((selectors: QueryList<InputSelectorComponent>) => {
      const selector = selectors.find(s => s.key === aCond);
      selector?.focus();
    });
  }
  removeCondition(pop: Population, blockIndex: number, condIndex: number) {
    const block = pop.blocks[blockIndex];
    const condition = block?.conditions[condIndex];
    if (!block || !condition) {
      return;
    }

    const blockDescriptor = pop.blocks.length > 1 ? ` from block ${blockIndex + 1}` : '';
    const conditionName = this.describeCondition(condition);

    block.conditions.splice(condIndex, 1);
    if (block.conditions.length === 0) {
      pop.blocks.splice(blockIndex, 1);
    }

    const message = `Removed condition ${condIndex + 1}${conditionName ? ` (${conditionName})` : ''}${blockDescriptor}.`;
    this.snackBar.open(message.trim(), 'Dismiss', {
      duration: 4000,
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }
  addBlock(pop: Population) {
    // const blockOrder = pop.blocks.length > 0 ? pop.blocks[pop.blocks.length - 1].order + 1 : 1;
    const aCond: Condition = {
      type: 'Question',
      // keyId?: string; // e.x. maps to questionId if NPCInput
      // formulaId?: string;
      // formula?: Formula;
      // question?: Question;
      format: 'Number',
      operator: 0,
      value: '',
      // value2?: string;     // used for "between" comparisons as upper bound
      // values?: string[];  // If value has comma separated values than split them into this array
      order: 1
    };
    const aBlock: Block = {
      blockOperatorAnd: true,
      conditions: [aCond]
    };
    pop.blocks.push(aBlock);

    this.inputSelectors.changes.pipe(take(1)).subscribe((selectors: QueryList<InputSelectorComponent>) => {
      const selector = selectors.find(s => s.key === aCond);
      selector?.focus();
    });
  }

  getConditionRowHeaderId(blockIndex: number, conditionIndex: number): string {
    return `${this.conditionHeaderPrefix}${blockIndex}-${conditionIndex}`;
  }

  getConditionRowId(blockIndex: number, conditionIndex: number): string {
    return `${this.conditionRowPrefix}${blockIndex}-${conditionIndex}`;
  }

  getRemoveConditionLabelId(blockIndex: number, conditionIndex: number): string {
    return `${this.removeConditionLabelPrefix}${blockIndex}-${conditionIndex}`;
  }

  getConditionControlLabelIds(
    field: 'question' | 'operator' | 'assertion',
    blockIndex: number,
    conditionIndex: number
  ): string {
    const columnHeaderId = this.columnHeaderMap[field];
    const rowHeaderId = this.getConditionRowHeaderId(blockIndex, conditionIndex);
    return `${columnHeaderId} ${rowHeaderId}`;
  }

  private describeCondition(condition: Condition): string {
    if (condition.type === 'Question') {
      return condition.question?.label ?? condition.keyId ?? '';
    }

    if (condition.type === 'Formula') {
      return condition.formula?.name ?? condition.formulaId ?? '';
    }

    if (condition.type === 'Calculation') {
      return this.getSelectorName(condition.keyId);
    }

    return '';
  }
}
